import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as eventService from "../services/eventService";
import * as rsvpService from "../services/rsvpService";

function etiquetaEstado(estado: string) {
  if (estado === "ASISTE") return "Asiste";
  if (estado === "NO_ASISTE") return "No asiste";
  if (estado === "PENDIENTE") return "Pendiente";
  return estado;
}

function claseEstado(estado: string) {
  if (estado === "ASISTE")
    return "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20";
  if (estado === "NO_ASISTE")
    return "bg-red-500/10 text-red-500 border border-red-500/20";
  if (estado === "PENDIENTE")
    return "bg-amber-500/10 text-amber-500 border border-amber-500/20";
  return "bg-slate-500/10 text-slate-500 border border-slate-500/20";
}

function etiquetaTipoGrupo(tipo: string) {
  if (tipo === "FAMILIA") return "Familia";
  if (tipo === "AMIGOS") return "Amigos";
  if (tipo === "INVITADOS_ESPECIALES") return "Invitados especiales";
  return tipo;
}

function etiquetaRelacion(item: rsvpService.RsvpResumenItem) {
  if (item.esPrincipal || item.relacion === "PRINCIPAL") return "Principal";
  return "Adicional";
}

function formatoFecha(iso: string | null) {
  if (!iso) return "Sin confirmar";
  try {
    return new Intl.DateTimeFormat("es-PE", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function porcentaje(valor: number, total: number) {
  if (!total) return 0;
  return Math.round((valor / total) * 100);
}

function escaparHtml(value: unknown) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

type ViewMode = "GRUPOS" | "LISTA";

type RsvpGrupoResumen = {
  grupoId: number;
  nombreGrupo: string;
  tipoGrupo: string;
  items: rsvpService.RsvpResumenItem[];
  asisten: number;
  noAsisten: number;
  pendientes: number;
};

function RsvpSummaryPage() {
  const navigate = useNavigate();
  const { eventoId } = useParams();
  const idEvento = Number(eventoId);

  const [eventoNombre, setEventoNombre] = useState("Evento");
  const [eventoFecha, setEventoFecha] = useState<string | null>(null);
  const [resumen, setResumen] = useState<rsvpService.RsvpResumen | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("TODOS");
  const [tipoGrupo, setTipoGrupo] = useState("TODOS");
  const [viewMode, setViewMode] = useState<ViewMode>("LISTA");

  async function cargar() {
    if (!Number.isFinite(idEvento)) return;

    setLoading(true);
    setErrorMsg(null);

    try {
      try {
        const ev = await eventService.obtenerEvento(idEvento);
        setEventoNombre(ev.nombre);
        setEventoFecha(ev.fechaPrincipal);
      } catch {
        // El resumen sigue siendo útil aunque el encabezado no pueda enriquecerse.
      }

      const data = await rsvpService.obtenerResumenRsvp(idEvento);
      setResumen(data);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Error cargando resumen RSVP");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idEvento]);

  const items = useMemo(() => resumen?.items ?? [], [resumen]);

  const tiposGrupo = useMemo(() => {
    return Array.from(new Set(items.map((item) => item.tipoGrupo))).sort();
  }, [items]);

  const filtrados = useMemo(() => {
    const query = q.trim().toLowerCase();
    return items.filter((item) => {
      const nombreCompleto = `${item.nombre ?? ""} ${item.apellido ?? ""}`.trim();
      const matchQuery =
        !query ||
        nombreCompleto.toLowerCase().includes(query) ||
        item.nombreGrupo.toLowerCase().includes(query) ||
        (item.telefono ?? "").toLowerCase().includes(query) ||
        (item.email ?? "").toLowerCase().includes(query);

      const matchEstado = estado === "TODOS" ? true : item.estado === estado;
      const matchTipo = tipoGrupo === "TODOS" ? true : item.tipoGrupo === tipoGrupo;
      return matchQuery && matchEstado && matchTipo;
    });
  }, [items, q, estado, tipoGrupo]);

  const gruposFiltrados = useMemo<RsvpGrupoResumen[]>(() => {
    const byGroup = new Map<number, RsvpGrupoResumen>();

    filtrados.forEach((item) => {
      const current =
        byGroup.get(item.grupoId) ??
        {
          grupoId: item.grupoId,
          nombreGrupo: item.nombreGrupo,
          tipoGrupo: item.tipoGrupo,
          items: [],
          asisten: 0,
          noAsisten: 0,
          pendientes: 0,
        };

      current.items.push(item);
      if (item.estado === "ASISTE") current.asisten += 1;
      else if (item.estado === "NO_ASISTE") current.noAsisten += 1;
      else current.pendientes += 1;
      byGroup.set(item.grupoId, current);
    });

    return Array.from(byGroup.values())
      .map((grupo) => ({
        ...grupo,
        items: [...grupo.items].sort((x, y) => {
          const xPrincipal = x.esPrincipal || x.relacion === "PRINCIPAL";
          const yPrincipal = y.esPrincipal || y.relacion === "PRINCIPAL";
          if (xPrincipal !== yPrincipal) return xPrincipal ? -1 : 1;
          return x.invitadoId - y.invitadoId;
        }),
      }))
      .sort((x, y) => x.grupoId - y.grupoId);
  }, [filtrados]);

  const totalInvitados = resumen?.totalInvitados ?? 0;
  const totalGestionados =
    (resumen?.asisten ?? 0) + (resumen?.noAsisten ?? 0) + (resumen?.pendientes ?? 0);
  const avance = porcentaje(totalGestionados - (resumen?.pendientes ?? 0), totalGestionados);

  function exportarExcel() {
    if (!resumen) return;

    const rows = filtrados.map((item) => {
      const nombreCompleto = `${item.nombre ?? ""} ${item.apellido ?? ""}`.trim() || "—";
      return [
        nombreCompleto,
        item.invitadoId,
        etiquetaRelacion(item),
        item.nombreGrupo,
        item.grupoId,
        etiquetaTipoGrupo(item.tipoGrupo),
        etiquetaEstado(item.estado),
        item.telefono || "—",
        item.email || "Sin email",
        formatoFecha(item.confirmadoEn),
        item.comentario || "—",
      ];
    });

    const tableRows = rows
      .map(
        (row) =>
          `<tr>${row.map((cell) => `<td>${escaparHtml(cell)}</td>`).join("")}</tr>`
      )
      .join("");

    const html = `
      <html>
        <head>
          <meta charset="utf-8" />
        </head>
        <body>
          <h2>Resumen RSVP - ${escaparHtml(eventoNombre)}</h2>
          <p>Evento ID: ${escaparHtml(resumen.eventoId)}</p>
          <p>Total invitados: ${escaparHtml(resumen.totalInvitados)} | Asisten: ${escaparHtml(
      resumen.asisten
    )} | No asisten: ${escaparHtml(resumen.noAsisten)} | Pendientes: ${escaparHtml(
      resumen.pendientes
    )}</p>
          <table border="1">
            <thead>
              <tr>
                <th>Invitado</th>
                <th>ID invitado</th>
                <th>Tipo invitado</th>
                <th>Grupo</th>
                <th>ID grupo</th>
                <th>Tipo grupo</th>
                <th>Estado</th>
                <th>Telefono</th>
                <th>Email</th>
                <th>Confirmado en</th>
                <th>Comentario</th>
              </tr>
            </thead>
            <tbody>${tableRows}</tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safeEventName = eventoNombre.toLowerCase().replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "");
    link.href = url;
    link.download = `reporte-rsvp-${safeEventName || resumen.eventoId}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:px-12">
      <div className="max-w-7xl mx-auto flex flex-col gap-8">
        <div className="flex flex-col gap-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <button
                type="button"
                onClick={() => navigate("/home/rsvp")}
                className="text-[#637588] dark:text-[#ab9db8] font-medium hover:text-primary transition-colors flex items-center gap-1"
              >
                <span className="material-symbols-outlined text-base">arrow_back</span>
                RSVP
              </button>

              <span className="material-symbols-outlined text-[#637588] dark:text-[#ab9db8] text-base">
                chevron_right
              </span>

              <span className="text-[#111418] dark:text-white font-semibold">Resumen</span>
            </div>

            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={exportarExcel}
                disabled={!resumen || filtrados.length === 0}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-surface-darker bg-gold-accent hover:bg-[#c5a028] border border-gold-accent/30 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="material-symbols-outlined text-[18px]">table_view</span>
                Exportar Excel
              </button>

              <button
                type="button"
                onClick={cargar}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors dark:bg-[#191121] dark:hover:bg-white/5"
              >
                <span className="material-symbols-outlined text-[18px]">refresh</span>
                Actualizar
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <h1 className="text-[#111418] dark:text-white text-2xl sm:text-3xl md:text-4xl font-black leading-tight tracking-tight">
              Resumen RSVP:{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-400">
                {eventoNombre}
              </span>
            </h1>
            <p className="text-[#637588] dark:text-[#ab9db8] text-base font-normal">
              Revisa confirmaciones, pendientes y comentarios enviados por cada invitado.
            </p>
          </div>
        </div>

        {loading && (
          <div className="text-[#637588] dark:text-[#ab9db8]">Cargando resumen RSVP...</div>
        )}
        {!loading && errorMsg && <div className="text-red-400">{errorMsg}</div>}

        {!loading && !errorMsg && resumen && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="flex flex-col gap-2 rounded-xl p-5 border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm">
                <div className="flex justify-between items-start">
                  <p className="text-[#637588] dark:text-[#ab9db8] text-sm font-medium uppercase tracking-wider">
                    Invitados
                  </p>
                  <span className="material-symbols-outlined text-primary">group</span>
                </div>
                <p className="text-[#111418] dark:text-white text-3xl font-bold leading-tight">
                  {resumen.totalInvitados}
                </p>
                <div className="text-sm text-[#637588] dark:text-[#ab9db8]">
                  {resumen.totalGrupos} grupos registrados
                </div>
              </div>

              <div className="flex flex-col gap-2 rounded-xl p-5 border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm">
                <div className="flex justify-between items-start">
                  <p className="text-[#637588] dark:text-[#ab9db8] text-sm font-medium uppercase tracking-wider">
                    Asisten
                  </p>
                  <span className="material-symbols-outlined text-emerald-500">check_circle</span>
                </div>
                <p className="text-[#111418] dark:text-white text-3xl font-bold leading-tight">
                  {resumen.asisten}
                </p>
                <div className="text-sm text-[#637588] dark:text-[#ab9db8]">
                  {porcentaje(resumen.asisten, totalInvitados)}% del total de invitados
                </div>
              </div>

              <div className="flex flex-col gap-2 rounded-xl p-5 border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm">
                <div className="flex justify-between items-start">
                  <p className="text-[#637588] dark:text-[#ab9db8] text-sm font-medium uppercase tracking-wider">
                    No asisten
                  </p>
                  <span className="material-symbols-outlined text-red-500">cancel</span>
                </div>
                <p className="text-[#111418] dark:text-white text-3xl font-bold leading-tight">
                  {resumen.noAsisten}
                </p>
                <div className="text-sm text-[#637588] dark:text-[#ab9db8]">
                  {porcentaje(resumen.noAsisten, totalInvitados)}% del total de invitados
                </div>
              </div>

              <div className="flex flex-col gap-2 rounded-xl p-5 border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm">
                <div className="flex justify-between items-start">
                  <p className="text-[#637588] dark:text-[#ab9db8] text-sm font-medium uppercase tracking-wider">
                    Pendientes
                  </p>
                  <span className="material-symbols-outlined text-amber-500">schedule</span>
                </div>
                <p className="text-[#111418] dark:text-white text-3xl font-bold leading-tight">
                  {resumen.pendientes}
                </p>
                <div className="text-sm text-[#637588] dark:text-[#ab9db8]">
                  Avance de confirmación: {avance}%
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 rounded-xl p-5 border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm">
                <div className="flex items-center justify-between gap-4 mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-[#111418] dark:text-white">
                      Estado general
                    </h3>
                    <p className="text-sm text-[#637588] dark:text-[#ab9db8]">
                      {resumen.totalConfirmados} asistentes confirmados en respuestas RSVP.
                    </p>
                  </div>
                  <span className="text-sm font-bold text-primary">{avance}%</span>
                </div>

                <div className="w-full bg-[#e0e0e0] dark:bg-[#362f40] h-3 rounded-full overflow-hidden flex">
                  <div
                    className="bg-emerald-500 h-3"
                    style={{ width: `${porcentaje(resumen.asisten, totalInvitados)}%` }}
                  />
                  <div
                    className="bg-red-500 h-3"
                    style={{ width: `${porcentaje(resumen.noAsisten, totalInvitados)}%` }}
                  />
                  <div
                    className="bg-amber-500 h-3"
                    style={{ width: `${porcentaje(resumen.pendientes, totalInvitados)}%` }}
                  />
                </div>

                <div className="mt-4 flex flex-wrap gap-4 text-sm text-[#637588] dark:text-[#ab9db8]">
                  <span className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-emerald-500" />
                    Asisten
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-red-500" />
                    No asisten
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-amber-500" />
                    Pendientes
                  </span>
                </div>
              </div>

              <div className="rounded-xl p-5 border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm">
                <div className="flex justify-between items-start">
                  <p className="text-[#637588] dark:text-[#ab9db8] text-sm font-medium uppercase tracking-wider">
                    Fecha del evento
                  </p>
                  <span className="material-symbols-outlined text-primary">calendar_month</span>
                </div>
                <p className="mt-2 text-[#111418] dark:text-white text-2xl font-bold leading-tight">
                  {eventoFecha
                    ? new Intl.DateTimeFormat("es-PE", {
                        year: "numeric",
                        month: "short",
                        day: "2-digit",
                      }).format(new Date(eventoFecha))
                    : "—"}
                </p>
                <p className="mt-2 text-sm text-[#637588] dark:text-[#ab9db8]">
                  Evento #{resumen.eventoId}
                </p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white dark:bg-[#231b2e] p-2 rounded-xl border border-[#e0e0e0] dark:border-[#473c53]">
              <div className="relative w-full md:min-w-[320px]">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[#637588] dark:text-[#ab9db8]">
                  search
                </span>
                <input
                  className="w-full bg-[#f7f6f8] dark:bg-[#191121] text-[#111418] dark:text-white pl-10 pr-4 py-2 rounded-lg border-none focus:ring-2 focus:ring-primary placeholder-[#637588] dark:placeholder-[#645970] text-sm"
                  placeholder="Buscar invitado, grupo, teléfono o email..."
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full md:w-auto">
                <select
                  className="bg-[#f7f6f8] dark:bg-[#191121] text-[#111418] dark:text-white px-3 py-2 rounded-lg border-none focus:ring-2 focus:ring-primary text-sm"
                  value={estado}
                  onChange={(e) => setEstado(e.target.value)}
                >
                  <option value="TODOS">Todos los estados</option>
                  <option value="ASISTE">Asiste</option>
                  <option value="NO_ASISTE">No asiste</option>
                  <option value="PENDIENTE">Pendiente</option>
                </select>

                <select
                  className="bg-[#f7f6f8] dark:bg-[#191121] text-[#111418] dark:text-white px-3 py-2 rounded-lg border-none focus:ring-2 focus:ring-primary text-sm"
                  value={tipoGrupo}
                  onChange={(e) => setTipoGrupo(e.target.value)}
                >
                  <option value="TODOS">Todos los grupos</option>
                  {tiposGrupo.map((tipo) => (
                    <option key={tipo} value={tipo}>
                      {etiquetaTipoGrupo(tipo)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex bg-[#f7f6f8] dark:bg-[#191121] p-1 rounded-lg w-full md:w-auto">
                <button
                  type="button"
                  onClick={() => setViewMode("GRUPOS")}
                  className={
                    viewMode === "GRUPOS"
                      ? "cursor-pointer flex-1 md:flex-none px-4 py-1.5 rounded-md bg-white dark:bg-[#302938] text-primary dark:text-white shadow-sm text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                      : "cursor-pointer flex-1 md:flex-none px-4 py-1.5 rounded-md text-[#637588] dark:text-[#ab9db8] text-sm font-medium flex items-center justify-center gap-2 transition-colors hover:text-primary"
                  }
                >
                  <span className="material-symbols-outlined text-[18px]">view_module</span>
                  <span>Grupos</span>
                </button>

                <button
                  type="button"
                  onClick={() => setViewMode("LISTA")}
                  className={
                    viewMode === "LISTA"
                      ? "cursor-pointer flex-1 md:flex-none px-4 py-1.5 rounded-md bg-white dark:bg-[#302938] text-primary dark:text-white shadow-sm text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                      : "cursor-pointer flex-1 md:flex-none px-4 py-1.5 rounded-md text-[#637588] dark:text-[#ab9db8] text-sm font-medium flex items-center justify-center gap-2 transition-colors hover:text-primary"
                  }
                >
                  <span className="material-symbols-outlined text-[18px]">view_list</span>
                  <span>Lista</span>
                </button>
              </div>
            </div>

            <div className="pb-10">
              {viewMode === "GRUPOS" && (
                <div className="rounded-xl border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b border-[#e0e0e0] dark:border-[#362f40]">
                    <h3 className="text-lg font-bold text-[#111418] dark:text-white">
                      Confirmaciones por grupo
                    </h3>
                    <p className="text-sm text-[#637588] dark:text-[#ab9db8]">
                      Mostrando {gruposFiltrados.length} grupos con {filtrados.length} registros
                    </p>
                  </div>

                  <div className="p-5">
                    {gruposFiltrados.length === 0 ? (
                      <div className="px-5 py-10 text-center text-[#637588] dark:text-[#ab9db8]">
                        No se encontraron grupos.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        {gruposFiltrados.map((grupo) => {
                          const principal = grupo.items.find((item) => item.esPrincipal || item.relacion === "PRINCIPAL");

                          return (
                            <div
                              key={grupo.grupoId}
                              className="rounded-xl border border-[#e0e0e0] dark:border-[#473c53] bg-[#fbfafc] dark:bg-[#191121] overflow-hidden"
                            >
                              <div className="p-4 border-b border-[#e0e0e0] dark:border-[#362f40] flex items-start justify-between gap-4">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs font-bold text-[#637588] dark:text-[#ab9db8]">
                                      Grupo #{grupo.grupoId}
                                    </span>
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-bold border border-primary/20">
                                      {etiquetaTipoGrupo(grupo.tipoGrupo)}
                                    </span>
                                  </div>
                                  <h4 className="mt-2 text-base font-bold text-[#111418] dark:text-white truncate">
                                    {grupo.nombreGrupo}
                                  </h4>
                                  <p className="mt-1 text-sm text-[#637588] dark:text-[#ab9db8] truncate">
                                    Principal:{" "}
                                    {principal
                                      ? `${principal.nombre ?? ""} ${principal.apellido ?? ""}`.trim() || "—"
                                      : "—"}
                                  </p>
                                </div>

                                <div className="text-right shrink-0">
                                  <p className="text-2xl font-bold text-[#111418] dark:text-white">
                                    {grupo.items.length}
                                  </p>
                                  <p className="text-xs uppercase tracking-wider text-[#637588] dark:text-[#ab9db8] font-bold">
                                    registros
                                  </p>
                                </div>
                              </div>

                              <div className="px-4 py-3 grid grid-cols-3 gap-2 border-b border-[#e0e0e0] dark:border-[#362f40]">
                                <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3 py-2">
                                  <p className="text-xs font-bold text-emerald-500">Asisten</p>
                                  <p className="text-lg font-bold text-[#111418] dark:text-white">{grupo.asisten}</p>
                                </div>
                                <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2">
                                  <p className="text-xs font-bold text-red-500">No asisten</p>
                                  <p className="text-lg font-bold text-[#111418] dark:text-white">{grupo.noAsisten}</p>
                                </div>
                                <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2">
                                  <p className="text-xs font-bold text-amber-500">Pendientes</p>
                                  <p className="text-lg font-bold text-[#111418] dark:text-white">{grupo.pendientes}</p>
                                </div>
                              </div>

                              <div className="p-3 divide-y divide-[#e0e0e0] dark:divide-[#362f40]">
                                {grupo.items.map((item) => {
                                  const nombreCompleto = `${item.nombre ?? ""} ${item.apellido ?? ""}`.trim() || "—";
                                  const esPrincipal = item.esPrincipal || item.relacion === "PRINCIPAL";

                                  return (
                                    <div key={item.invitadoId} className="py-3 first:pt-0 last:pb-0 flex items-center justify-between gap-3">
                                      <div className="flex items-center gap-3 min-w-0">
                                        <div className="size-9 rounded-full bg-white dark:bg-[#302938] border border-[#e0e0e0] dark:border-[#473c53] flex items-center justify-center shrink-0">
                                          <span className="material-symbols-outlined text-primary text-[20px]">
                                            {esPrincipal ? "stars" : "person"}
                                          </span>
                                        </div>
                                        <div className="min-w-0">
                                          <p className="text-sm font-semibold text-[#111418] dark:text-white truncate">
                                            {nombreCompleto}
                                          </p>
                                          <p className="text-xs text-[#637588] dark:text-[#ab9db8] truncate">
                                            #{item.invitadoId} · {etiquetaRelacion(item)} · {item.telefono || "Sin teléfono"}
                                          </p>
                                          {item.comentario && (
                                            <p className="mt-1 text-xs text-[#637588] dark:text-[#ab9db8] line-clamp-1">
                                              {item.comentario}
                                            </p>
                                          )}
                                        </div>
                                      </div>

                                      <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold shrink-0 ${claseEstado(
                                          item.estado
                                        )}`}
                                      >
                                        {etiquetaEstado(item.estado)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {viewMode === "LISTA" && (
                <div className="rounded-xl border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-[#e0e0e0] dark:border-[#362f40]">
                  <h3 className="text-lg font-bold text-[#111418] dark:text-white">
                    Confirmaciones por invitado
                  </h3>
                  <p className="text-sm text-[#637588] dark:text-[#ab9db8]">
                    Mostrando {filtrados.length} de {items.length} registros
                  </p>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-[#f7f6f8] dark:bg-[#191121] text-[#637588] dark:text-[#ab9db8]">
                      <tr>
                        <th className="text-left font-semibold uppercase tracking-wider px-5 py-3">
                          Invitado
                        </th>
                        <th className="text-left font-semibold uppercase tracking-wider px-5 py-3">
                          Grupo
                        </th>
                        <th className="text-left font-semibold uppercase tracking-wider px-5 py-3">
                          Estado
                        </th>
                        <th className="text-left font-semibold uppercase tracking-wider px-5 py-3">
                          Contacto
                        </th>
                        <th className="text-left font-semibold uppercase tracking-wider px-5 py-3">
                          Confirmado
                        </th>
                        <th className="text-left font-semibold uppercase tracking-wider px-5 py-3">
                          Comentario
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-[#e0e0e0] dark:divide-[#362f40]">
                      {filtrados.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-5 py-10 text-center text-[#637588] dark:text-[#ab9db8]"
                          >
                            No se encontraron confirmaciones.
                          </td>
                        </tr>
                      ) : (
                        filtrados.map((item) => {
                          const nombreCompleto =
                            `${item.nombre ?? ""} ${item.apellido ?? ""}`.trim() || "—";

                          return (
                            <tr
                              key={item.invitadoId}
                              className="hover:bg-black/5 dark:hover:bg-white/5"
                            >
                              <td className="px-5 py-4 text-[#111418] dark:text-white">
                                <div className="font-semibold">{nombreCompleto}</div>
                                <div className="text-xs text-[#637588] dark:text-[#ab9db8]">
                                  #{item.invitadoId} · {etiquetaRelacion(item)}
                                </div>
                              </td>

                              <td className="px-5 py-4 text-[#111418] dark:text-white">
                                <div className="font-medium">{item.nombreGrupo}</div>
                                <div className="text-xs text-[#637588] dark:text-[#ab9db8]">
                                  {etiquetaTipoGrupo(item.tipoGrupo)} · Grupo #{item.grupoId}
                                </div>
                              </td>

                              <td className="px-5 py-4">
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${claseEstado(
                                    item.estado
                                  )}`}
                                >
                                  {etiquetaEstado(item.estado)}
                                </span>
                              </td>

                              <td className="px-5 py-4 text-[#111418] dark:text-white">
                                <div>{item.telefono || "—"}</div>
                                <div className="text-xs text-[#637588] dark:text-[#ab9db8]">
                                  {item.email || "Sin email"}
                                </div>
                              </td>

                              <td className="px-5 py-4 text-[#111418] dark:text-white">
                                {formatoFecha(item.confirmadoEn)}
                              </td>

                              <td className="px-5 py-4 text-[#111418] dark:text-white max-w-[280px]">
                                <span className="line-clamp-2">
                                  {item.comentario || "—"}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default RsvpSummaryPage;
